/** O que as três telas do Painel de Diárias compartilham: constantes, formatação,
 *  o vocabulário do ERP e as peças de célula.
 *
 *  Extraído da tela única de 02/09/2026 quando ela virou três (Samuel, 03/09/2026):
 *  Painel de Diárias, Painel de Lançamento e Painel de Conferência. As duas últimas
 *  vivem em `DiariasGradePage`; a primeira, em `PainelDiariasPage`. */
import type { ReactNode } from 'react'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import { alpha } from '@mui/material/styles'

export const PER_PAGE = 50

/** Teto do período, o mesmo que o servidor aplica em `JANELA_MAX_DIAS`.
 *
 *  Repetir o número aqui é duplicação consciente: o servidor precisa da guarda
 *  contra uma URL montada à mão, e a tela precisa impedir ANTES de a pessoa
 *  escolher. Recusar depois da escolha é o pior dos dois mundos: ela já decidiu, e
 *  a tela some para dizer não. */
export const JANELA_MAX_DIAS = 180

/** Quantos dias à frente de hoje o Painel de Lançamento e o atalho Lançar Diária
 *  alcançam, o mesmo `JANELA_A_FRENTE_DIAS` do servidor. "Pode ser uns 15 dias para
 *  frente, mas o filtro vai vim já filtrado na data atual" (Samuel, 28/09/2026). */
export const DIAS_A_FRENTE = 15

export const hoje = () => new Date().toISOString().slice(0, 10)

export function somaDias(iso: string, dias: number): string {
  const d = new Date(`${iso}T00:00:00`)
  d.setDate(d.getDate() + dias)
  return d.toISOString().slice(0, 10)
}

/** "2026-08-12" → "12/08/2026".
 *
 *  O ANO FICA, ao contrário do Histórico da jornada, e a diferença é o alcance: lá o
 *  período é de trinta dias e o ano seria repetição; aqui o banco guarda desde 2021,
 *  o filtro alcança 180 dias e a mesma tela mostra 05/06 de dois anos diferentes.
 *  Sem o ano, ordenar por data produziria uma sequência que não se explica. */
export const diaCurto = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`

/** "2026-08-12" → "12/08", para o lugar onde o ano é o mesmo da tela inteira. */
export const diaEMes = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`

/** "2026-09-03" → "quarta", no idioma da tela. A coluna Dia que o Samuel pediu ao
 *  lado da Data (03/09/2026): "dia 3 do nove, quarta-feira" é mais fácil de ler. */
export function diaDaSemana(iso: string, lang: string): string {
  const locale = lang === 'en' ? 'en-US' : lang === 'es' ? 'es-ES' : 'pt-BR'
  const nome = new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString(locale, { weekday: 'long' })
  // "quarta-feira" → "quarta": o hífen é separador de layout aqui, não ortografia.
  const curto = nome.replace(/-feira$/i, '')
  return curto.charAt(0).toUpperCase() + curto.slice(1)
}

export const horaDe = (iso: string) => new Date(iso).toLocaleTimeString('pt-BR', {
  timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
})

/** Instante ISO → "12/08/2026 14:05", no fuso da operação. */
export const dataHoraDe = (iso: string) => {
  const d = new Date(iso)
  const dia = d.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })
  return `${dia} ${horaDe(iso)}`
}

/** "TRANSGRAOS - PARACATU" → "PARACATU", como no Histórico da jornada: o prefixo é
 *  igual em todas as linhas e ocupa a coluna inteira sem informar nada. */
export function semPrefixoDaEmpresa(base: string | null | undefined): string | null {
  if (!base) return null
  const corte = base.match(/^\s*TRANSGR[ÃA]OS\s*[-–—]\s*(.+)$/i)
  return corte ? corte[1].trim() : base
}

export const moeda = (valor: number | null | undefined) => (valor == null
  ? null
  : valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))

/** A faixa que cada calendário aceita, derivada do outro campo.
 *
 *  O de não pode passar do até, o até não pode ficar antes do de, e a distância
 *  entre eles não pode passar do teto. Com isso o dia inválido nasce desabilitado
 *  no calendário, e o erro do servidor deixa de ser alcançável pela tela. */
export function limitesDoPeriodo(de: string, ate: string) {
  return {
    deMin: somaDias(ate, -JANELA_MAX_DIAS),
    deMax: ate,
    ateMin: de,
    ateMax: somaDias(de, JANELA_MAX_DIAS),
  }
}

export const vazio = <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>

/** `quebra` acompanha o `wrap` da coluna, e os dois PRECISAM concordar.
 *
 *  Truncar com reticências exige `nowrap`, e `nowrap` num span inline não é
 *  recortado por nada: o texto simplesmente sai por cima da coluna vizinha. É o
 *  que acontecia com `PROGRAMACAO - FORA DA BASE (MAIOR QUE 30KM)`, que tem 43
 *  caracteres e cobria as duas colunas seguintes. */
export function texto(valor: string | null | undefined, cor = 'text.primary', quebra = false): ReactNode {
  if (!valor) return vazio
  return (
    <Box
      component="span"
      sx={quebra
        ? { color: cor, display: 'block', minWidth: 0, overflowWrap: 'break-word' }
        : { color: cor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
    >
      {valor}
    </Box>
  )
}

export const numero = (valor: string | null | undefined): ReactNode => (valor
  ? <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>{valor}</Box>
  : vazio)

/** O chip pequeno da identidade do HUB, na coluna do responsável. `dica` quando o
 *  chip precisa explicar que a decisão foi da rotina e não de uma pessoa. */
export function chipHub(rotulo: string, dica?: string): ReactNode {
  const chip = (
    <Box
      component="span"
      sx={{
        display: 'inline-flex', px: 0.625, py: 0.0625, borderRadius: '0 8px 0 8px',
        fontWeight: 700, fontSize: '10px', lineHeight: 1.4, flex: 'none',
        bgcolor: alpha('#00552C', 0.14), color: 'primary.main',
        cursor: dica ? 'help' : 'default',
      }}
    >
      {rotulo}
    </Box>
  )
  return dica ? <Tooltip title={dica} arrow>{chip}</Tooltip> : chip
}

/** Um chip de estado, com o matiz e o texto que a tela decidir. */
export function chip(rotulo: string, hue: string, fg: string, dica?: ReactNode): ReactNode {
  const corpo = (
    <Box
      component="span"
      sx={{
        display: 'inline-flex', alignItems: 'center', px: 0.75, py: 0.125,
        borderRadius: '0 8px 0 8px', fontWeight: 700, fontSize: '11px', lineHeight: 1.35,
        whiteSpace: 'nowrap', bgcolor: alpha(hue, 0.14), color: fg,
        cursor: dica ? 'help' : 'default',
      }}
    >
      {rotulo}
    </Box>
  )
  return dica ? <Tooltip title={dica} arrow>{corpo}</Tooltip> : corpo
}

export interface Opcao {
  value: string
  label: string
}

export const opcoesDe = (valores: string[] | undefined, rotulo: (v: string) => string = (v) => v): Opcao[] =>
  (valores ?? []).map((v) => ({ value: v, label: rotulo(v) }))
